// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { zipSync } from 'fflate';
import { escapeHtml, toCsv } from './domain/csv';
import { SHAPE_RULES } from './domain/shapes';
import type { Match, Note, RecoveredFile, Redacted, ScanResult } from './types';

/**
 * The four artefacts.
 *
 * ONE DOWNLOAD PER EXPLICIT CLICK. Chrome's download limiter starts a page at ALLOW_ONE_DOWNLOAD and
 * flips to PROMPT_BEFORE_DOWNLOAD on the SECOND programmatic download; if the user misses the
 * bubble, artefacts two, three and four silently never arrive. Four buttons is not a worse UX than
 * one button, and it is the only shape that works in every browser without a permission prompt.
 *
 * Every builder takes `Redacted<Match>[]` and nothing else — handing one a raw `Match[]` is a
 * compile error. The single exception is `recovered-source.zip`, which is unmaskable by
 * construction and says so in-product: it is a verbatim copy of what your own sourcemaps publish,
 * which is the finding.
 */

const enc = new TextEncoder();

/** Filenames are FIXED STRING CONSTANTS. Channel 6: interpolating a project or variable name puts it
 * in ~/Downloads, shell history, screen recordings and cloud-synced folders. */
export const FILENAMES = {
  report: 'exposure-report.html',
  csv: 'rotate-these.csv',
  source: 'recovered-source.zip',
  fix: 'fix.md',
} as const;

function severityRank(s: Note['severity']): number {
  return { critical: 0, high: 1, medium: 2, low: 3, expected: 4, info: 5 }[s];
}

function locationText(m: Match): string {
  if (!m.location) return `byte ${m.byteOffset.toLocaleString('en-GB')}`;
  const base = `byte ${m.byteOffset.toLocaleString('en-GB')} (line ${m.location.generatedLine + 1}, col ${m.location.utf16Column})`;
  switch (m.location.outcome) {
    case 'mapped':
      return `${base} → ${m.location.source}:${m.location.originalLine}:${m.location.originalColumn}`;
    case 'approximate':
      return `${base} → near ${m.location.source}:${m.location.originalLine} (approximate — past the last mapped position on this line)`;
    case 'unmapped':
      return `${base} → no original location (this region of the file is not covered by the sourcemap)`;
    case 'refused-index-map':
      return `${base} → original location refused (index map)`;
    default:
      return base;
  }
}

function kindText(m: Match): string {
  switch (m.kind) {
    case 'literal-in-source':
      return 'written literally in your source';
    case 'build-inlined':
      return 'substituted at build time — the mapped location is the expression that was replaced, not where your secret lives';
    default:
      return 'no sourcemap for this file';
  }
}

/**
 * `rotate-these.csv` — one row per credential, with the provider's own rotation URL.
 *
 * The rotation URLs are plain links with no reachability claim attached: re-measured with a browser
 * user agent, several of them do not return 200 to an automated client at all (Hugging Face 401s,
 * the AWS one 405s to HEAD and carries a fragment that is never sent, OpenAI 403s behind bot
 * protection). Asserting "verified" about them would be a claim we cannot support.
 */
export function buildCsv(result: ScanResult): string {
  const rows: string[][] = [];
  const seen = new Set<string>();
  for (const m of result.matches) {
    const key = `${m.envKey}|${m.ruleId ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const rule = SHAPE_RULES.find((r) => r.id === m.ruleId);
    const where = result.matches.filter((x) => x.envKey === m.envKey).map((x) => x.path);
    rows.push([
      m.envKey,
      m.masked,
      rule?.label ?? (m.ruleId ?? 'from your .env'),
      String(new Set(where).size),
      [...new Set(where)].slice(0, 5).join(' | '),
      kindText(m),
      rule?.rotateUrl ?? '',
    ]);
  }
  return toCsv(
    ['variable_or_rule', 'masked_value', 'kind', 'files_containing_it', 'files', 'how_it_got_there', 'rotation_url'],
    rows,
  );
}

/** `fix.md` — remediation generated from the same redacted matches. Snippets are TEMPLATES. */
export function buildFix(result: ScanResult): string {
  const byVar = new Map<string, Redacted<Match>[]>();
  for (const m of result.matches) {
    const list = byVar.get(m.envKey);
    if (list) list.push(m);
    else byVar.set(m.envKey, [m]);
  }

  const lines: string[] = [
    '# What to do about this',
    '',
    'Generated by leakmap. Every value below is masked — leakmap has no reveal control, and this file',
    'is safe to attach to a ticket. It is **not** a statement that these keys still work: leakmap never',
    'contacts a provider, because doing so would mean transmitting your secret to one.',
    '',
    '## 1. Rotate first, then fix the build',
    '',
    'Rotation is cheap and reversible; working out whether someone already took the key is neither.',
    '',
  ];

  for (const [name, ms] of byVar) {
    const rule = SHAPE_RULES.find((r) => r.id === ms[0]!.ruleId);
    lines.push(`- **${name}** — ${ms[0]!.masked}, in ${new Set(ms.map((m) => m.path)).size} file(s).`);
    if (rule?.rotateUrl) lines.push(`  Rotate at <${rule.rotateUrl}>`);
    if (rule?.note) lines.push(`  ${rule.note}`);
  }

  lines.push(
    '',
    '## 2. Stop the value reaching the client',
    '',
    'A variable with a public prefix is **compiled into your JavaScript by design**. Renaming it is the',
    'fix; hiding it is not.',
    '',
    '```diff',
    '- VITE_OPENAI_API_KEY=...      # inlined into the bundle',
    '+ OPENAI_API_KEY=...           # server-only, never reaches the client',
    '```',
    '',
    'Then move the call behind a server route or an edge function and read it as `process.env.OPENAI_API_KEY`',
    'there. If the SDK made you pass `dangerouslyAllowBrowser: true`, that flag is the SDK telling you the',
    'same thing.',
    '',
    '## 3. Stop shipping sourcemaps, if you did not mean to',
    '',
    '```js',
    '// vite.config.ts',
    'export default defineConfig({ build: { sourcemap: false } })',
    '',
    '// next.config.js',
    'module.exports = { productionBrowserSourceMaps: false }',
    '',
    '// webpack',
    'module.exports = { devtool: false }',
    '',
    '// esbuild / rollup',
    '// drop --sourcemap / output.sourcemap',
    '```',
    '',
    '## 4. Check the deployed origin too',
    '',
    'leakmap read a folder on your machine, which may not be what is deployed. A host can advertise a',
    'sourcemap with a `SourceMap:` response header even when no comment appears in the file, and that',
    'header takes precedence over the comment. Check the response headers on your live origin as well.',
    '',
  );

  if (result.skipped.length) {
    lines.push('## Values leakmap did not search for', '');
    for (const s of result.skipped) lines.push(`- \`${s.key}\` — ${s.reason}`);
    lines.push('');
  }
  return lines.join('\n');
}

/**
 * `exposure-report.html` — one self-contained file, no external anything.
 *
 * Every interpolated string goes through `escapeHtml`: context spans, file paths and sourcemap
 * `sources` entries are all third-party-controlled and this file gets opened from `file://`.
 */
export function buildReport(result: ScanResult, generatedAt: string): string {
  const notes = [...result.notes].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
  const rows = result.matches
    .map(
      (m) => `<tr>
      <td><code>${escapeHtml(m.envKey)}</code></td>
      <td class="mask">${escapeHtml(m.masked)}</td>
      <td><code>${escapeHtml(m.path)}</code><div class="loc">${escapeHtml(locationText(m))}</div></td>
      <td>${escapeHtml(kindText(m))}${m.variantId === 'raw' ? '' : `<div class="loc">matched as: ${escapeHtml(m.variantId)}</div>`}</td>
      <td class="ctx"><span>${escapeHtml(m.beforeRedacted)}</span><b>[value]</b><span>${escapeHtml(m.afterRedacted)}</span></td>
    </tr>`,
    )
    .join('\n');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>leakmap exposure report</title>
<style>
:root{color-scheme:light dark}
body{font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;margin:0;padding:2rem;max-width:1100px}
h1{font-size:1.4rem;margin:0 0 .25rem}
.sub{color:#666;margin:0 0 1.5rem}
table{border-collapse:collapse;width:100%;margin:1rem 0;font-size:13px}
th,td{border-bottom:1px solid #ccc;padding:.5rem;text-align:left;vertical-align:top}
th{font-weight:600;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#666}
code{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-size:12px;word-break:break-all}
.mask{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;white-space:nowrap}
.loc{color:#777;font-size:11.5px;margin-top:.2rem;font-family:ui-monospace,Menlo,monospace;word-break:break-all}
.ctx{font-family:ui-monospace,Menlo,monospace;font-size:11.5px;color:#555;word-break:break-all;max-width:24rem}
.ctx b{background:#ffe9a8;color:#000;padding:0 .2rem;border-radius:2px}
.note{border-left:3px solid #999;padding:.5rem .75rem;margin:.5rem 0;background:rgba(127,127,127,.07)}
.note.critical{border-color:#c62828}.note.high{border-color:#e65100}.note.medium{border-color:#f9a825}
.note.expected{border-color:#2e7d32}.note.info{border-color:#777}
.note h3{margin:0 0 .25rem;font-size:13px}
.note p{margin:0;color:#555;font-size:12.5px}
.honesty{border:1px solid #999;padding:1rem;border-radius:6px;margin:1.5rem 0;font-size:13px}
@media (prefers-color-scheme:dark){body{background:#0b0f14;color:#e6edf3}.sub,.loc,.ctx,.note p,th{color:#9aa7b4}
th,td{border-color:#233}.ctx b{background:#5c4a00;color:#ffe9a8}}
</style></head><body>
<h1>leakmap exposure report</h1>
<p class="sub">Generated ${escapeHtml(generatedAt)} · ${result.matches.length} occurrence(s) · ${result.assets.scanned} file(s) searched</p>

<div class="honesty">
<strong>What this report is.</strong> leakmap searched the files listed below for the literal values in the
.env you gave it, and for a small set of well-known credential shapes. Every value here is masked, and
leakmap has no reveal control.
<br><br>
<strong>What it is not.</strong> leakmap never contacted OpenAI, Stripe, AWS, Supabase or any other provider,
so it does not know whether any of these keys still work — rotate them anyway. An absence of matches means
the values were not found <em>verbatim</em>, not that the build is clean. leakmap read a folder, not a
deployment.
</div>

${notes.length ? `<h2>Findings</h2>${notes.map((n) => `<div class="note ${n.severity}"><h3>${escapeHtml(n.title)}</h3><p>${escapeHtml(n.detail)}${n.path ? ` <code>${escapeHtml(n.path)}</code>` : ''}</p></div>`).join('\n')}` : ''}

${result.matches.length ? `<h2>Occurrences</h2><table><thead><tr><th>Variable / rule</th><th>Value</th><th>Where</th><th>How</th><th>Context</th></tr></thead><tbody>${rows}</tbody></table>` : '<h2>No verbatim match</h2><p>See the caveats above — this is not a clean bill of health.</p>'}

${result.skipped.length ? `<h2>Values leakmap did not search for</h2><table><thead><tr><th>Variable</th><th>Why</th></tr></thead><tbody>${result.skipped.map((s) => `<tr><td><code>${escapeHtml(s.key)}</code></td><td>${escapeHtml(s.reason)}</td></tr>`).join('')}</tbody></table>` : ''}
</body></html>`;
}

/**
 * `recovered-source.zip` — the proof that ends the argument about whether the exposure is
 * theoretical.
 *
 * fflate emits no ZIP64 and, above 4 GiB, silently wraps the central-directory offset rather than
 * throwing; and its own reader returns a WRONG SUBSET above 65,535 entries with no error. So leakmap
 * imposes its own limits well below both and refuses with an explanation rather than writing a file
 * that looks complete and is not.
 */
export const ZIP_MAX_ENTRIES = 60_000;
export const ZIP_MAX_BYTES = 3 * 1024 * 1024 * 1024;

export function buildSourceZip(files: readonly RecoveredFile[]): Uint8Array {
  if (files.length > ZIP_MAX_ENTRIES) {
    throw new Error(
      `${files.length.toLocaleString('en-GB')} recovered files is past the ${ZIP_MAX_ENTRIES.toLocaleString('en-GB')} leakmap will put in one archive. ` +
        'Above 65,535 entries the ZIP end-of-directory count wraps, and some readers then return a partial archive with no error at all.',
    );
  }
  const tree: Record<string, Uint8Array> = {};
  let total = 0;
  for (const f of files) {
    const bytes = enc.encode(f.content);
    total += bytes.length;
    if (total > ZIP_MAX_BYTES) {
      throw new Error(
        'The recovered source is over 3 GiB. leakmap refuses rather than writing it, because the ZIP writer emits no ZIP64 and would silently corrupt the archive above 4 GiB instead of failing.',
      );
    }
    tree[f.path] = bytes;
  }
  tree['LEAKMAP-README.txt'] = enc.encode(
    [
      'This archive is your own original source code, recovered from the sourcemaps in the build you',
      'gave leakmap.',
      '',
      'IT IS NOT MASKED, AND IT CANNOT BE. That is the point: everything in here is what your own',
      'sourcemaps already publish to anyone who downloads them. It may contain credentials that were',
      'never in your .env, comments you would not publish, and file paths carrying your username and',
      'home directory.',
      '',
      'Treat it as the thing you are trying to stop shipping.',
      '',
      'Nothing in this archive was uploaded anywhere. It was assembled in your browser.',
    ].join('\n'),
  );
  return zipSync(tree, { level: 6 });
}

/**
 * Trigger exactly one download.
 *
 * `showSaveFilePicker` is a Chromium-only upgrade (Firefox and Safari have never shipped it), so the
 * anchor is the universal path. `AbortError` is swallowed: a user cancelling the picker must not
 * fall through into a second, unwanted download. Revocation is delayed because Safari loses the
 * download if the object URL is revoked immediately.
 */
export async function saveOne(filename: string, data: string | Uint8Array, mime: string): Promise<void> {
  const blob = new Blob([data as BlobPart], { type: mime });

  const picker = (window as unknown as { showSaveFilePicker?: (o: object) => Promise<FileSystemFileHandle> })
    .showSaveFilePicker;
  if (typeof picker === 'function') {
    try {
      // `types` is omitted deliberately: `{ [mime]: ['.' + ext] }` throws TypeError on a dotless
      // filename, and the suggested name already carries the extension.
      const handle = await picker({ suggestedName: filename });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      return;
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      // Any other failure falls through to the anchor.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
